/**
 * Event pricing store – single source of truth for artist event pricing.
 * Session-based fetch like personal details: ensureFetched() runs GET once per session.
 */

import { create } from "zustand";
import { ArtistService } from "@/services/artistService";
import { useArtistDetailsStore } from "@/stores/artistDetailsStore";
import type {
  EventPricingApiItem,
  EventPricingCreatePayload,
  EventPricingUpdatePayload,
} from "@/services/artistService";

export type { EventPricingApiItem, EventPricingCreatePayload, EventPricingUpdatePayload };

interface EventPricingState {
  data: EventPricingApiItem[];
  isLoading: boolean;
  error: string | null;
  hasFetchedInSession: boolean;

  setData: (data: EventPricingApiItem[]) => void;
  fetchEventPricing: () => Promise<EventPricingApiItem[]>;
  /** Fetches only if !hasFetchedInSession. Call when user opens the section. */
  ensureFetched: () => Promise<EventPricingApiItem[]>;
  createEventPricing: (payload: EventPricingCreatePayload) => Promise<void>;
  updateEventPricing: (slug: string, payload: EventPricingUpdatePayload) => Promise<void>;
  deleteEventPricing: (slug: string) => Promise<void>;
  patchSpecialEventPricing: (slug: string, payload: { festival_pricing: Record<string, string> }) => Promise<void>;
  deleteSpecialEventPricing: (slug: string) => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  /** Call on logout to reset session cache. */
  reset: () => void;
}

let inFlightFetch: Promise<EventPricingApiItem[]> | null = null;

const initialState = {
  data: [],
  isLoading: false,
  error: null,
  hasFetchedInSession: false,
};

export const useEventPricingStore = create<EventPricingState>()((set, get) => ({
  ...initialState,

  setData: (data) => set({ data, error: null }),

  fetchEventPricing: async () => {
    if (inFlightFetch) return inFlightFetch;
    inFlightFetch = (async () => {
      set({ isLoading: true, error: null });
      try {
        const data = await ArtistService.getEventPricing();
        const list = Array.isArray(data) ? data : [];
        set({
          data: list,
          error: null,
          isLoading: false,
          hasFetchedInSession: true,
        });
        void useArtistDetailsStore.getState().fetchArtistDetails();
        return list;
      } catch (err) {
        set({
          error: "Failed to load event pricing",
          isLoading: false,
          data: [],
        });
        return [];
      } finally {
        inFlightFetch = null;
      }
    })();
    return inFlightFetch;
  },

  ensureFetched: async () => {
    const { hasFetchedInSession, fetchEventPricing, data } = get();
    if (hasFetchedInSession) return data;
    return fetchEventPricing();
  },

  createEventPricing: async (payload) => {
    set({ error: null });
    try {
      await ArtistService.createEventPricing(payload);
      await get().fetchEventPricing();
    } catch (err) {
      set({ error: "Failed to add event. Please try again." });
      throw err;
    }
  },

  updateEventPricing: async (slug, payload) => {
    set({ error: null });
    try {
      await ArtistService.updateEventPricing(slug, payload);
      await get().fetchEventPricing();
    } catch (err) {
      set({ error: "Failed to update. Please try again." });
      throw err;
    }
  },

  deleteEventPricing: async (slug) => {
    set({ error: null });
    try {
      await ArtistService.deleteEventPricing(slug);
      await get().fetchEventPricing();
    } catch (err) {
      set({ error: "Failed to delete. Please try again." });
      throw err;
    }
  },

  patchSpecialEventPricing: async (slug, payload) => {
    set({ error: null });
    try {
      await ArtistService.patchSpecialEventPricing(slug, payload);
      await get().fetchEventPricing();
    } catch (err) {
      set({ error: "Failed to update occasion prices. Please try again." });
      throw err;
    }
  },

  deleteSpecialEventPricing: async (slug) => {
    set({ error: null });
    try {
      await ArtistService.deleteSpecialEventPricing(slug);
      await get().fetchEventPricing();
    } catch (err) {
      set({ error: "Failed to remove special pricing. Please try again." });
      throw err;
    }
  },

  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),

  reset: () => {
    inFlightFetch = null;
    set(initialState);
  },
}));
